<template>
    <v-container class="py-7">
        <v-row>
            <v-col cols="12">
                <div class="title-container">
                    <div class="title-block">
                        <span id="title" class="page-title">Diário de Classe</span>
                        <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbItems"></v-breadcrumbs>
                    </div>
                </div>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="filter-card">
                    <v-card-title class="filter-card-title">Filtro</v-card-title>
                    <v-card-text>
                        <v-form @submit.prevent="loadAttendances()">
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-select dense label="Turma" :items="dataset.classes" item-text="name" item-value="id"
                                        v-model="filter.class_id" outlined hide-details clearable
                                        @change="loadClassSchedules" />
                                </v-col>
                                <v-col cols="12" md="5">
                                    <v-select dense label="Aula" :items="dataset.schedules" item-text="label" item-value="id"
                                        v-model="filter.schedule_id" outlined hide-details clearable />
                                </v-col>
                                <v-col cols="12" md="3">
                                    <v-text-field dense label="Data" type="date" v-model="filter.date" outlined hide-details />
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12">
                                    <v-btn id="btnFilter" type="submit" color="primary" dark elevation="2">Filtrar</v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card class="table-card">
                    <v-card-title class="table-card-title">Presenças</v-card-title>
                    <v-card-text>
                        <v-data-table :headers="headers" :items="attendances" :loading="loading"
                            disable-sort hide-default-footer no-data-text="Selecione uma turma, aula e data"
                            loading-text="Carregando alunos..." class="app-table hover-table">

                            <template v-slot:[`item.presence`]="{ item }">
                                <v-chip label small :color="item.presence === true ? 'success' : item.presence === false ? 'error' : ''">
                                    {{ presenceLabel(item.presence) }}
                                </v-chip>
                            </template>

                            <template v-slot:[`item.actions`]="{ item }">
                                <v-btn-toggle v-if="$check('Attendance', 'POST')" dense :value="item.presence" @change="setPresence(item, $event)">
                                    <v-btn small :value="true">Presente</v-btn>
                                    <v-btn small :value="false">Falta</v-btn>
                                </v-btn-toggle>
                                <span v-else class="text--secondary">Somente leitura</span>
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from "axios";

const weekDays = {
    1: "Segunda-feira",
    2: "Terça-feira",
    3: "Quarta-feira",
    4: "Quinta-feira",
    5: "Sexta-feira",
    6: "Sábado",
    7: "Domingo",
};

export default {
    name: "AttendanceList",

    data: () => ({
        breadcrumbItems: [
            { text: "Homepage", disabled: false, to: "/home", exact: true },
            { text: "Diário de Classe", disabled: true, exact: true },
        ],
        filter: {
            class_id: null,
            schedule_id: null,
            date: null,
        },
        dataset: {
            classes: [],
            schedules: [],
        },
        headers: [
            { text: "Aluno", value: "name" },
            { text: "Status", value: "presence" },
            { text: "Registro", value: "actions", sortable: false },
        ],
        attendances: [],
        loading: false,
    }),

    created() {
        this.filter.date = this.$moment().format("YYYY-MM-DD");
        this.loadClasses();
    },

    methods: {
        async loadClasses() {
            try {
                const result = await axios.get("/api/v1/classes", { params: { page: 1, limit: 100000, search: JSON.stringify({}) } });
                this.dataset.classes = result.data.content.data;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        async loadClassSchedules() {
            this.filter.schedule_id = null;
            this.attendances = [];
            this.dataset.schedules = [];

            if (!this.filter.class_id) return;

            try {
                const result = await axios.get(`/api/v1/classes/${this.filter.class_id}`);
                const data = result.data.content;

                this.dataset.schedules = (data.schedules || []).map((schedule) => ({
                    ...schedule,
                    label: this.scheduleLabel(schedule),
                }));
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },

        scheduleLabel(schedule) {
            const day = weekDays[schedule.day] || "Dia";
            const course = schedule.course ? schedule.course.name : "Componente curricular";
            const teacher = schedule.teacher ? schedule.teacher.name : "Professor";

            return `${day} - ${course} - ${teacher} (${schedule.start_time} às ${schedule.end_time})`;
        },

        async loadAttendances() {
            if (!this.filter.schedule_id || !this.filter.date) {
                this.$dialog.notify.error("Selecione uma aula e uma data.", { position: "top-right", timeout: 2000 });
                return;
            }

            this.loading = true;

            try {
                const result = await axios.get("/api/v1/attendances", {
                    params: {
                        schedule_id: this.filter.schedule_id,
                        date: this.filter.date,
                    },
                });

                this.attendances = result.data.content.data.map((item) => ({
                    ...item,
                    presence: item.presence === null || item.presence === undefined ? null : Boolean(item.presence),
                }));
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            } finally {
                this.loading = false;
            }
        },

        presenceLabel(value) {
            if (value === true || value === 1) return "Presente";
            if (value === false || value === 0) return "Falta";
            return "Pendente";
        },

        async setPresence(item, presence) {
            if (!item || presence === undefined || presence === null) return;

            try {
                await axios.post("/api/v1/attendances/action", {
                    schedule_id: this.filter.schedule_id,
                    student_id: item.id,
                    presence,
                    date: this.filter.date,
                });

                item.presence = presence;
                this.$dialog.notify.info("Presença atualizada.", { position: "top-right", timeout: 1200 });
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
            }
        },
    },
};
</script>
